import React, { useEffect, useState } from 'react';
import { InstantSearch, connectHits, connectSearchBox, Configure, Pagination } from 'react-instantsearch-dom';  // Import Pagination
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import { Card, Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import {
    FaUsers, FaTshirt, FaPalette, FaLeaf, FaTag, FaThLarge, FaUser, FaCalendarAlt, FaBarcode, FaMoneyBillAlt, FaTrash, FaSearch
} from 'react-icons/fa';
import moment from 'moment';
import 'moment/locale/zh-tw';
import '../App.css'; // or './index.css'
import config from './config'; // Import config

moment.locale('zh-tw');

// Configure the Typesense InstantSearch adapter
const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
    server: {
        apiKey: config.typesenseApiKey, // Use API key from config
        nodes: [
            {
                host: config.typesenseHost,  // Host from config
                port: config.typesensePort,  // Port from config
                protocol: config.typesenseProtocol, // Protocol from config
            },
        ],
    },
    additionalSearchParameters: {
        query_by: 'embedding, description',
        vector_query: "embedding:([], alpha: 0.8)",
        hitsPerPage: 1, // Increase this from 1 to 20 to match ConstructionSearch
        prefix: 'false',
        exclude_fields: 'embedding',
    }
});

const searchClient = typesenseInstantsearchAdapter.searchClient;

// Custom Hit component to display search results with highlights and image
const Hit = ({ hit, onImageClick }) => {
    // console.log(hit);

    const handleDelete = () => {
        fetch(`${config.typesenseProtocol}://${config.typesenseHost}/collections/clothing/documents/${hit.id}`, {
            method: 'DELETE',
            headers: {
                'X-TYPESENSE-API-KEY': config.typesenseApiKey,
                'Content-Type': 'application/json'
            }
        })
            .then(response => response.json())
            .then(data => {
                console.log('Document deleted:', data);
                window.location.reload();  // Optionally: refresh the page or use InstantSearch's refresh function
            })
            .catch(error => {
                console.error('Error deleting document:', error);
            });
    };

    const isDangerous = hit.anyone_not_wearing_helmets || hit.people_smoking || (hit.dangerous_environment && hit.dangerous_environment !== "無");

    // Use thumbnail_url if available, otherwise fall back to image_url
    const displayImageUrl = hit.thumbnail_url || hit.image_url;

    return (
        <Col xs={12} sm={6} md={4} lg={3} xl={2} xxl={1} className="mb-4" style={{ minWidth: '300px' }}>
            <Card className="h-100 position-relative" style={{ border: '1px solid #ddd', borderRadius: '5px', backgroundColor: isDangerous ? '#f8d7da' : '#fff' }}>
                {displayImageUrl && (
                    <Card.Img
                        variant="top"
                        src={displayImageUrl}
                        alt={hit.description}
                        style={{ maxHeight: '200px', objectFit: 'cover', cursor: 'pointer' }}
                        onClick={() => onImageClick(hit.image_url || displayImageUrl)}  // Use full-size image in modal
                    />
                )}
                <Card.Body>
                    <Card.Title className="text-primary">{hit.main_category} - {hit.type}</Card.Title>
                    <Card.Text style={{ fontSize: '0.75rem' }}>{hit.description}</Card.Text>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {/* Number of People */}
                        {hit.number_of_people && (
                            <span className="d-flex align-items-center gap-2">
                                <FaUsers style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.number_of_people}
                            </span>
                        )}

                        {/* Clothing type */}
                        <span className="d-flex align-items-start gap-2">
                            <FaTshirt style={{ color: '#007bff', fontSize: '1rem', flexShrink: 0 }} />
                            <span style={{ fontSize: '1rem', lineHeight: '1.2' }}>{hit.main_category}</span>
                        </span>

                        {/* Colors */}
                        <span className="d-flex align-items-center gap-2">
                            <FaPalette style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.colors.join(', ')}
                        </span>

                        {/* Material */}
                        <span className="d-flex align-items-center gap-2">
                            <FaLeaf style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.material}
                        </span>

                        {/* Style */}
                        <span className="d-flex align-items-center gap-2">
                            <FaTag style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.style}
                        </span>

                        {/* Pattern */}
                        {hit.pattern && (
                            <span className="d-flex align-items-center gap-2">
                                <FaThLarge style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.pattern || '無'}
                            </span>
                        )}

                        {/* Usage */}
                        <span className="d-flex align-items-center gap-2">
                            <FaUser style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.usage}
                        </span>

                        {/* Timestamp */}
                        <span className="d-flex align-items-center gap-2">
                            <FaCalendarAlt style={{ color: '#007bff', fontSize: '1rem' }} /> {moment(hit.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                        </span>

                        {/* Order ID */}
                        <span className="d-flex align-items-center gap-2">
                            <FaBarcode style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.order_id}
                        </span>

                        {/* Price */}
                        <span className="d-flex align-items-center gap-2">
                            <FaMoneyBillAlt style={{ color: '#007bff', fontSize: '1rem' }} /> {hit.price}
                        </span>
                    </div>
                </Card.Body>

                {/* Add delete button in the bottom right corner */}
                <button
                    onClick={handleDelete}
                    style={{
                        position: 'absolute',
                        bottom: '10px',
                        right: '10px',
                        backgroundColor: 'transparent',
                        border: 'none',
                        cursor: 'pointer'
                    }}
                >
                    <FaTrash style={{ color: 'grey' }} />
                </button>
            </Card>
        </Col>
    );
};

// Custom Hits component using connectHits
const CustomHits = ({ hits, onImageClick }) => {
    console.log('Search results:', hits);  // Add console log to see search results

    return (
        <Row className="mt-4 hits-container">
            {hits.map(hit => (
                <Hit key={hit.objectID} hit={hit} onImageClick={onImageClick} />
            ))}
        </Row>
    );
};

const ConnectedHits = connectHits(CustomHits);

// Custom search box component using connectSearchBox
const CustomSearchBox = ({ currentRefinement, refine }) => {
    // Reference to the search input
    const searchInputRef = React.useRef(null);

    // Get URL parameters on component mount
    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const descriptionParam = urlParams.get('description');

        if (descriptionParam) {
            refine(descriptionParam);
            // Focus the search input
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }
    }, [refine]);

    return (
        <Form onSubmit={e => e.preventDefault()}>
            <InputGroup className="mb-3">
                <Form.Control
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search..."
                    aria-label="Search"
                    value={currentRefinement}
                    onChange={e => refine(e.currentTarget.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            window.location.reload();  // Reload the page when 'Enter' is pressed
                        }
                    }}
                />
                <Button variant="outline-secondary" onClick={() => refine(currentRefinement)}>
                    <FaSearch />
                </Button>
            </InputGroup>
        </Form>
    );
};

const ConnectedSearchBox = connectSearchBox(CustomSearchBox);

function ClothingSearch() {
    const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState('');
    const [initialSearchDone, setInitialSearchDone] = useState(false);

    const handleImageClick = (imageUrl) => {
        setModalImage(imageUrl);
        setShowModal(true);
    };

    const handleCloseModal = () => setShowModal(false);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter') {
                window.location.reload();  // Reload the page when Enter key is pressed
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        // Check for URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const descriptionParam = urlParams.get('description');

        if (descriptionParam && !initialSearchDone) {
            // We'll handle this in the CustomSearchBox component
            setInitialSearchDone(true);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyDown); // Clean up event listener on unmount
        };
    }, [initialSearchDone]);

    return (
        <div className="container-fluid mt-4 text-center">
            <h1>AI洗衣智慧監看</h1>
            <p className="text-center text-danger small">請按Enter更新資料</p>

            <InstantSearch searchClient={searchClient} indexName="clothing">
                <Configure hitsPerPage={20} />  {/* Set number of results per page */}
                <ConnectedSearchBox />
                <ConnectedHits onImageClick={handleImageClick} />

                {/* Pagination Component */}
                <Pagination showLast={true} />  {/* Adds pagination control */}
            </InstantSearch>

            {/* Custom modal implementation without using Bootstrap Modal */}
            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 2000,
                    }}
                    onClick={handleCloseModal}
                >
                    <div
                        style={{
                            position: 'relative',
                            backgroundColor: 'white',
                            padding: '20px',
                            borderRadius: '5px',
                            maxWidth: '90%',
                            maxHeight: '90%',
                            minWidth: '40%',
                            overflow: 'auto',
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={handleCloseModal}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                backgroundColor: 'black',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 2001
                            }}
                        >
                            ✕
                        </button>
                        <div style={{
                            marginTop: '20px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                            height: '100%'
                        }}>
                            <img
                                src={modalImage}
                                alt="Enlarged view"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: 'calc(90vh - 80px)',
                                    objectFit: 'contain',
                                    display: 'block'
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClothingSearch;
